import React from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import primaryNavigation from 'config/navigation';
import { Link as ExternalLink } from '@resideo/blueprint-react';
import { useCurrentUser } from 'hooks/useCurrentUser';
import profileMenuItems from 'config/profileMenu';

interface StyledMobileMenu {
  show: boolean;
}

const StyledMobileMenu = styled.div<StyledMobileMenu>`
  display: ${({ show }) => (show ? 'block' : 'none')};
  margin: ${({ theme }) => theme.space.medium} 0;
  width: 100%;
  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    display: none;
  }
`;

const MenuItem = styled(({ isExternalLink, ...props }) =>
  isExternalLink ? <ExternalLink {...props} /> : <NavLink {...props} />
)`
  display: block;
  width: 100%;
  text-decoration: none;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.header.primaryLink};
  padding: ${({ theme }) => `${theme.space.medium} ${theme.space.small}`};
  &:hover {
    color: ${({ theme }) => theme.colors.header.primaryLinkActive};
  }
  &.active {
    color: ${({ theme }) => theme.colors.header.primaryLinkActive};
  }
`;

const MobileMenuToggleContainer = styled.div`
  display: block;
  margin-top: ${({ theme }) => theme.space.xSmall};
  margin-left: ${({ theme }) => theme.space.medium};
  @media (min-width: ${({ theme }) => theme.breakpoints.large}) {
    display: none;
  }
`;

const StyledMobileMenuToggle = styled.div`
  display: inline-block;
  cursor: pointer;
  outline: none;
  div {
    width: 20px;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.header.primaryLink};
    margin: ${({ theme }) => theme.space.xSmall} 0;
    transition: 0.4s;
  }
`;

const MobileMenu = ({
  show,
  onClick,
}: {
  show: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  const [{ isOwner, isAdmin }] = useCurrentUser();
  const viewerHasPermissions = isOwner || isAdmin;

  return (
    <StyledMobileMenu show={show}>
      {primaryNavigation.map((data): JSX.Element | null => {
        if (!data.linkPath || !data.linkTextKey) return null;
        return !data.isExternalLink ? (
          <MenuItem
            key={data.name}
            to={data.linkPath}
            exact={true}
            onClick={onClick}>
            {t(data.linkTextKey)}
          </MenuItem>
        ) : (
          <MenuItem
            key={data.name}
            href={data.linkPath}
            isExternalLink={data.isExternalLink}>
            {t(data.linkTextKey)}
          </MenuItem>
        );
      })}
      {profileMenuItems(viewerHasPermissions).map(
        (data): JSX.Element | null => {
          if (!data.linkPath || !data.linkTextKey) return null;
          return !data.isExternalLink ? (
            <MenuItem
              key={`mobile-${data.name}`}
              to={data.linkPath}
              onClick={onClick}>
              {t(data.linkTextKey)}
            </MenuItem>
          ) : (
            <MenuItem
              key={`mobile-${data.name}`}
              href={data.linkPath}
              isExternalLink={data.isExternalLink}>
              {t(data.linkTextKey)}
            </MenuItem>
          );
        }
      )}
    </StyledMobileMenu>
  );
};

const MobileMenuToggle = ({
  onClick,
}: {
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void;
}) => {
  return (
    <MobileMenuToggleContainer>
      <StyledMobileMenuToggle onClick={onClick} role='button' tabIndex={0}>
        <div></div>
        <div></div>
        <div></div>
      </StyledMobileMenuToggle>
    </MobileMenuToggleContainer>
  );
};

export { MobileMenu as default, MobileMenuToggle };
